import classnamesBind from "classnames/bind";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import useReturnNotesSummary from "./hooks/useReturnNotesSummary";
import useReturnNotesSummaryFeedback, {
  RETURN_NOTES_SUMMARY_FEEDBACK,
  RETURN_NOTES_SUMMARY_FEEDBACK_SHELL_ID,
} from "./hooks/useReturnNotesSummaryFeedback";
import styles from "./ReturnNotesSummary.scss";

const classnames = classnamesBind.bind(styles);

type RuntimeConstantsShape = {
  getTranslation: (translationKey: string) => string;
};

const ReturnNotesSummary = () => {
  const { getTranslation } = useRuntimeConstants() as RuntimeConstantsShape;
  const returnNotesSummary = useReturnNotesSummary();
  const { isEnabled, selectedFeedback, onThumbsUp, onThumbsDown } =
    useReturnNotesSummaryFeedback({ returnNotesSummary });

  if (!returnNotesSummary) {
    return null;
  }

  const { disclaimer, summary } = returnNotesSummary;

  return (
    <section
      className={styles.sectionContainer}
      aria-labelledby="returnNotesTitle"
    >
      <div
        className={styles.maxWidthContainer}
        data-testid="returnNotesSection"
      >
        <div
          className={classnames(styles.wrapper)}
          data-testid="return-notes-summary"
        >
          <h3 className={styles.title} id="returnNotesTitle">
            {getTranslation("pdp_ratings_reviews_return_reason_summary_title")}
          </h3>
          <p className={styles.summary}>{summary}</p>
          <div className={styles.disclaimerRow}>
            <div
              className={classnames(styles.disclaimerWrapper, {
                disclaimerWrapperWithFeedback: isEnabled,
              })}
            >
              <span
                className={classnames(
                  styles.disclaimerIcon,
                  "product-ai-summary-icon",
                )}
              ></span>
              <p className={styles.disclaimer}>{disclaimer}</p>
            </div>
            {isEnabled && (
              <div
                className={styles.feedbackShell}
                id={RETURN_NOTES_SUMMARY_FEEDBACK_SHELL_ID}
                tabIndex={-1}
                data-testid="return-notes-feedback-shell"
              >
                <div
                  className={styles.feedbackButtons}
                  data-testid="return-notes-feedback"
                >
                  <button
                    type="button"
                    className={classnames(styles.feedbackButton, {
                      feedbackButtonSelected:
                        selectedFeedback === RETURN_NOTES_SUMMARY_FEEDBACK.UP,
                      feedbackButtonHidden:
                        selectedFeedback === RETURN_NOTES_SUMMARY_FEEDBACK.DOWN,
                    })}
                    onClick={selectedFeedback ? undefined : onThumbsUp}
                    aria-pressed={
                      selectedFeedback === RETURN_NOTES_SUMMARY_FEEDBACK.UP
                    }
                    aria-hidden={
                      selectedFeedback === RETURN_NOTES_SUMMARY_FEEDBACK.DOWN
                    }
                    tabIndex={
                      selectedFeedback === RETURN_NOTES_SUMMARY_FEEDBACK.DOWN
                        ? -1
                        : 0
                    }
                    disabled={Boolean(selectedFeedback)}
                    aria-label={getTranslation(
                      "pdp_ratings_reviews_return_reason_summary_thumbs_up",
                    )}
                    data-testid="return-notes-feedback-thumbs-up"
                  >
                    <span
                      className={classnames(
                        styles.feedbackIcon,
                        "product-thumbs-up",
                      )}
                    ></span>
                  </button>
                  <button
                    type="button"
                    className={classnames(styles.feedbackButton, {
                      feedbackButtonSelected:
                        selectedFeedback === RETURN_NOTES_SUMMARY_FEEDBACK.DOWN,
                      feedbackButtonHidden:
                        selectedFeedback === RETURN_NOTES_SUMMARY_FEEDBACK.UP,
                    })}
                    onClick={selectedFeedback ? undefined : onThumbsDown}
                    aria-pressed={
                      selectedFeedback === RETURN_NOTES_SUMMARY_FEEDBACK.DOWN
                    }
                    aria-hidden={
                      selectedFeedback === RETURN_NOTES_SUMMARY_FEEDBACK.UP
                    }
                    tabIndex={
                      selectedFeedback === RETURN_NOTES_SUMMARY_FEEDBACK.UP
                        ? -1
                        : 0
                    }
                    disabled={Boolean(selectedFeedback)}
                    aria-label={getTranslation(
                      "pdp_ratings_reviews_return_reason_summary_thumbs_down",
                    )}
                    data-testid="return-notes-feedback-thumbs-down"
                  >
                    <span
                      className={classnames(
                        styles.feedbackIcon,
                        "product-thumbs-down",
                      )}
                    ></span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReturnNotesSummary;
